import React, { useEffect, useMemo, useState } from "react"
import { IconArrowUpRight } from "@tabler/icons-react"
import type { IAnnouncement } from "@dc-ireland/shared"
import apiClient from "../../lib/api"
import LandingAnnouncementModal from "./LandingAnnouncementModal"
import LandingReveal from "./LandingReveal"
import LandingSectionHeader from "./LandingSectionHeader"
import {
  announcementTagLabel,
  announcementTagStyle,
  defaultAnnouncements,
  parseAnnouncementCategories,
} from "./landingAnnouncements"

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:3001"

const resolveImageUrl = (url?: string) => {
  if (!url) return undefined
  if (url.startsWith("http") || url.startsWith("//")) return url
  if (url.startsWith("/uploads")) return `${API_BASE_URL}${url}`
  return url
}

const formatDate = (value: Date | string) =>
  new Date(value).toLocaleDateString("en-IE", {
    day: "numeric",
    month: "short",
    year: "numeric",
  })

type ActiveModal = {
  title: string
  body: string
  categories: string[]
  dateLabel: string
  imageUrl?: string
}

const CategoryBadges = ({ categories }: { categories: string[] }) => (
  <div className="flex flex-wrap items-center gap-2">
    {categories.map((category) => (
      <span
        key={category}
        className={`rounded-full px-2.5 py-1 font-dm-sans text-[10px] font-semibold uppercase tracking-wider ${announcementTagStyle(category)}`}
      >
        {announcementTagLabel(category)}
      </span>
    ))}
  </div>
)

const LandingNewsSection: React.FC<{ standalone?: boolean }> = ({ standalone = false }) => {
  const [items, setItems] = useState<IAnnouncement[]>(defaultAnnouncements)
  const [activeModal, setActiveModal] = useState<ActiveModal | null>(null)

  useEffect(() => {
    apiClient.getAnnouncements().then((data) => {
      if (data.length > 0) setItems(data)
    })
  }, [])

  const { featured, rest } = useMemo(() => {
    const featuredItem = items.find((item) => item.featured) ?? items[0]
    const restItems = items.filter((item) => item.slug !== featuredItem?.slug)
    return { featured: featuredItem, rest: restItems }
  }, [items])

  const openItem = (item: IAnnouncement) => {
    if (item.linkUrl) {
      if (item.linkUrl.startsWith("mailto:") || item.linkUrl.startsWith("http")) {
        window.location.href = item.linkUrl
      } else {
        window.location.href = item.linkUrl
      }
      return
    }

    if (item.body) {
      setActiveModal({
        title: item.title,
        body: item.body,
        categories: parseAnnouncementCategories(item.category),
        dateLabel: formatDate(item.publishedAt ?? new Date()),
        imageUrl: resolveImageUrl(item.imageUrl),
      })
    }
  }

  if (!featured) return null

  const featuredCategories = parseAnnouncementCategories(featured.category)
  const featuredImage = resolveImageUrl(featured.imageUrl)

  return (
    <section
      id={standalone ? undefined : "news-and-announcements"}
      className={`relative overflow-hidden bg-white ${standalone ? "py-16 md:py-20" : "scroll-mt-24 py-20"}`}
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_100%_0%,rgba(214,229,255,0.35),transparent_50%)]" />

      <div className="relative mx-auto flex max-w-6xl flex-col gap-10 px-4 md:px-8 lg:px-20">
        {!standalone && (
          <LandingSectionHeader
            eyebrow="Stay connected"
            title="News & Announcements"
            description="Updates from across Dominion City Ireland."
          />
        )}

        <LandingReveal>
          <article className="group relative overflow-hidden rounded-2xl border border-[#ebebeb]/80 bg-[#fafafa] shadow-[0_12px_40px_-16px_rgba(0,43,122,0.1)] transition duration-300 hover:-translate-y-0.5 hover:shadow-[0_20px_50px_-16px_rgba(0,43,122,0.14)] lg:flex">
            <div className="absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-[#9e7e43] via-dc-primary-900 to-[#D6E5FF]" />

            {featuredImage && (
              <div className="relative h-48 shrink-0 overflow-hidden bg-white lg:h-auto lg:w-[38%]">
                <img
                  src={featuredImage}
                  alt=""
                  className="h-full w-full object-contain object-center p-4 transition duration-500 group-hover:scale-[1.02]"
                />
              </div>
            )}

            <div className="flex flex-1 flex-col gap-4 p-6 md:p-8">
              <div className="flex flex-wrap items-center gap-3">
                <CategoryBadges categories={featuredCategories} />
                <span className="rounded-full bg-[#9e7e43]/10 px-2.5 py-1 font-dm-sans text-[10px] font-semibold uppercase tracking-wider text-[#9e7e43]">
                  Featured
                </span>
                <time className="font-dm-sans text-sm text-[#787878]">
                  {formatDate(featured.publishedAt ?? new Date())}
                </time>
              </div>

              <div className="flex flex-col gap-2">
                <h3 className="font-outfit text-2xl font-semibold tracking-tight text-[#2d1a45] md:text-3xl">
                  {featured.title}
                </h3>
                <p className="font-dm-sans text-base leading-7 text-[#5a5a5a]">{featured.excerpt}</p>
              </div>

              {(featured.body || featured.linkUrl) && (
                <button
                  type="button"
                  onClick={() => openItem(featured)}
                  className="inline-flex w-fit items-center gap-1.5 font-dm-sans text-sm font-semibold tracking-wide text-[#9e7e43] transition group-hover:gap-2.5"
                >
                  {featured.linkLabel ?? "Read more"}
                  <IconArrowUpRight size={14} stroke={2} />
                </button>
              )}
            </div>
          </article>
        </LandingReveal>

        {rest.length > 0 && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-5">
            {rest.map((item, index) => {
              const categories = parseAnnouncementCategories(item.category)
              const imageUrl = resolveImageUrl(item.imageUrl)

              return (
                <LandingReveal key={item.slug} delay={index * 0.06}>
                  <article className="group flex h-full flex-col overflow-hidden rounded-xl border border-[#ebebeb]/80 bg-white shadow-[0_6px_24px_-10px_rgba(0,43,122,0.08)] transition duration-300 hover:-translate-y-0.5 hover:border-[#9e7e43]/20 hover:shadow-[0_12px_32px_-12px_rgba(0,43,122,0.12)]">
                    {imageUrl && (
                      <div className="aspect-[16/10] overflow-hidden">
                        <img
                          src={imageUrl}
                          alt=""
                          className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
                        />
                      </div>
                    )}
                    <div className="flex flex-1 flex-col gap-3 p-5">
                    <div className="flex items-center justify-between gap-2">
                      <CategoryBadges categories={categories} />
                      <time className="font-dm-sans text-xs text-[#9a9a9a]">
                        {formatDate(item.publishedAt ?? new Date())}
                      </time>
                    </div>

                    <h4 className="font-outfit text-lg font-semibold leading-snug tracking-tight text-[#2d1a45]">
                      {item.title}
                    </h4>
                    <p className="line-clamp-3 flex-1 font-dm-sans text-sm leading-6 text-[#5a5a5a]">
                      {item.excerpt}
                    </p>

                    {(item.body || item.linkUrl) && (
                      <button
                        type="button"
                        onClick={() => openItem(item)}
                        className="inline-flex w-fit items-center gap-1 font-dm-sans text-sm font-semibold text-dc-primary-900 transition group-hover:gap-2"
                      >
                        {item.linkLabel ?? "Read more"}
                        <IconArrowUpRight size={13} stroke={2} />
                      </button>
                    )}
                    </div>
                  </article>
                </LandingReveal>
              )
            })}
          </div>
        )}
      </div>

      {activeModal && (
        <LandingAnnouncementModal
          open={Boolean(activeModal)}
          onClose={() => setActiveModal(null)}
          title={activeModal.title}
          body={activeModal.body}
          categories={activeModal.categories}
          dateLabel={activeModal.dateLabel}
          imageUrl={activeModal.imageUrl}
        />
      )}
    </section>
  )
}

export default LandingNewsSection
